import React, {useState, useContext, useMemo} from 'react';
import TableAntD from "../../common/TableAntD";
import Button from "../../common/Button";
import axios from "../../common/axios";
import dayjs from 'dayjs';
import {UserContext} from "../../common/UserContext";
import {Popconfirm, Button as AntdButton} from 'antd';
import {DeleteRounded, EditRounded} from "@mui/icons-material";
import Tooltip from "../../common/Tooltip";

const ActivityReportSummaryScheduled = ({ refetchTrigger: externalRefetchTrigger = 0 }) => {
    const {user} = useContext(UserContext);
    const [refetchTrigger, setRefetchTrigger] = useState(0);

    const handleDelete = async (record) => {
        try {
            await axios.delete(`/web/ui/report/teacher/activity/deleteSchedule/${record.activitySummaryId}`);
            displayToastMessage('Schedule deleted successfully', null, 'success');
            setRefetchTrigger(prev => prev + 1);
        } catch (error) {
            console.error('Error deleting schedule:', error);
            displayToastMessage(error.response?.data?.error || 'Failed to delete schedule', null, 'error');
        }
    };

    const formatDateRange = (record) => {
        if (record.dateRangeType === 'adaptive') {
            return `Previous ${record.adaptiveRangeValue} ${record.adaptiveRangeUnit?.toLowerCase()}`;
        } else if (record.startDate && record.endDate) {
            const start = dayjs(record.startDate).format(user?.getDateFormatString?.() || 'DD/MM/YYYY');
            const end = dayjs(record.endDate).format(user?.getDateFormatString?.() || 'DD/MM/YYYY');
            return `${start} - ${end}`;
        }
        return '-';
    };

    const formatFrequency = (record) => {
        if (record.frequencyValue && record.frequencyUnit) {
            return `Every ${record.frequencyValue} ${record.frequencyUnit?.toLowerCase()}`;
        }
        return '-';
    };

    const columns = useMemo(() => [
        {
            title: 'Name',
            dataIndex: 'reportName',
            key: 'reportName',
            render: (text) => <strong>{text}</strong>,
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            render: (text) => text || '-',
        },
        {
            title: 'Class',
            dataIndex: 'className',
            key: 'className',
        },
        {
            title: 'Date Range',
            key: 'dateRange',
            render: (_, record) => formatDateRange(record),
        },
        {
            title: 'Frequency',
            key: 'frequency',
            render: (_, record) => formatFrequency(record),
        },
        {
            title: 'Next Run',
            dataIndex: 'nextRunDate',
            key: 'nextRunDate',
            width: 150,
            render: (date) => {
                if (!date) return '-';
                return dayjs(date).format(user?.getDateFormatString?.() || 'DD/MM/YYYY');
            }
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 80,
            render: (_, record) => (
                <Popconfirm
                    title="Delete this schedule?"
                    description="This will permanently remove the scheduled report."
                    onConfirm={() => handleDelete(record)}
                    okText="Delete"
                    cancelText="Cancel"
                    okButtonProps={{ danger: true }}
                >
                    <Tooltip title="Delete schedule">
                        <Button
                            type="text"
                            icon={<DeleteRounded />}
                        />
                    </Tooltip>
                </Popconfirm>
            ),
        },
    ], [user]);

    const fetchData = (pagingParams, handleTableData, handlePagingData, abortController) => {
        axios.get('/web/ui/report/teacher/activity/userScheduledSummaries', {
            signal: abortController.signal
        })
            .then(response => {
                const data = response.data || [];
                handleTableData(data);
                handlePagingData({
                    pageSize: pagingParams.pageSize,
                    totalResults: data.length,
                });
            })
            .catch(error => {
                if (error.name !== 'CanceledError') {
                    console.error('Error fetching scheduled summaries:', error);
                    handleTableData([]);
                    handlePagingData({
                        pageSize: pagingParams.pageSize,
                        totalResults: 0
                    });
                }
            });
    };

    return (
        <div>
            <TableAntD
                dynamicWidth={true}
                columnData={columns}
                scroll={{x: true}}
                pagingDependencies={[refetchTrigger, externalRefetchTrigger]}
                pagingData={{
                    fetchFunction: fetchData,
                    params: {pageSize: 20}
                }}
                rowKey="activitySummaryId"
                locale={{emptyText: 'No scheduled summaries'}}
            />
        </div>
    );
};

export default ActivityReportSummaryScheduled;